import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useManualQuery } from "graphql-hooks";

import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  Avatar,
  Card,
  CardMedia,
  CardContent,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import ReactPlayer from "react-player";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import ModeCommentOutlinedIcon from "@mui/icons-material/ModeCommentOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import AuthWrapper from "./AuthWrapper";
import FloatingMenu from "./components/FloatingMenu";
import videoImg from "./assets/video-img.png";
import sampleAvatar from "./assets/SearchResults/Avatars/Sarah_White.png";
import AlertPopup from "./components/AlertPopup";
import { isValidUUIDv4 } from "./hooks/uuid";

import { 
  VIDEO_BY_UUID,
  EDIT_VIDEO_MUTATION 
} from "./graphQL/video_qry";

export default function EditVideo() {
  const { videoUuid } = useParams(); // video UUID
  const navigate = useNavigate();
  const location = useLocation(); 

  const validateUrl = (url) => {
    const pattern = new RegExp(
      "^(https?:\\/\\/)?" +                                // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain
      "((\\d{1,3}\\.){3}\\d{1,3}))" +                      // OR IPv4
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" +                 // port/path
      "(\\?[;&a-z\\d%_.~+=-]*)?" +                        // query
      "(\\#[-a-z\\d_]*)?$","i"                            // fragment
    );
    return !!pattern.test(url);
  };

  let userUuid = localStorage.getItem("userUuid") ? localStorage.getItem("userUuid") : null;

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [duration, setDuration] = useState(0);
  // const [videoUrlError, setVideoUrlError] = useState(
  //   (videoData?.videoUrl && !validateUrl(videoData?.videoUrl)) || "");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showFullVideo, setShowFullVideo] = useState(false);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [videoData, setVideoData] = useState({});
  const [alertPopup, setAlertPopup] = useState({
    open: false,    
  });
  const [isInvalidURL, setIsInvalidURL] = useState(false);
  let role = localStorage.getItem("role") ? localStorage.getItem("role") : null;

  // Form validation errors
  const [errors, setErrors] = useState({
    title: "",
    content: "",
    url: "",    
  });

  const [getVideoData] = useManualQuery(VIDEO_BY_UUID, {
    fetchPolicy: "network-only",
  });
  const [editVideo] = useMutation(EDIT_VIDEO_MUTATION, {
    awaitRefetchQueries: true,
  });
  
  const loadVideoInfo = async () => {    
    if (!videoUuid) {
      setIsInvalidURL(true);
      return;
    }
    if (isValidUUIDv4(videoUuid)){
        const {
          loading: loadingVideoData,
          error: errorVideoData,
          data: videoData,
        } = await getVideoData({
          variables: {
            uuid: videoUuid,
          },
        });
        if (!loadingVideoData && 
            videoData && videoData.videoByUuid) {
          {/* check if login-user is not owner display error message */}
          if (role === "Admin" || videoData.videoByUuid.userId.userUuid === userUuid){
            setVideoData(videoData.videoByUuid);
            setTitle(videoData.videoByUuid.title);
            setContent(videoData.videoByUuid.content);
            setVideoUrl(videoData.videoByUuid.videoUrl); 
            setDuration(videoData.videoByUuid.duration);            
          } else {
            setIsInvalidURL(true);                 
          }
        }
        else
          setIsInvalidURL(true);
        if (errorVideoData) {
          console.log("errorCurrVideoData: ", errorVideoData);  
          setIsInvalidURL(true);
        }
    }
    else
      setIsInvalidURL(true);
  };

  useEffect(() => {
    (async () => loadVideoInfo())();
  }, [videoUuid]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
  }, []);  

  const handleSave = async(publish) => {
    const newErrors = {};    
    if (!title.trim()) {
      newErrors.title = "Title cannot be blank.";      
    };
    if (!content.trim()) {
      newErrors.content = "Video Text cannot be blank.";
    };
    if (!videoUrl.trim()) {
      newErrors.url = "Video URL cannot be blank.";
    };
    
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const {
      loading: loadEditVideo,
      error: errorEditVideo,
      data: dataEditVideo,
    } = await editVideo({
      variables: {
        videoUuid: videoUuid,
        title: title,
        content: content,
        videoUrl: videoUrl,
        duration: 0,
        isPublished: publish,        
      },
    });

    if (
      !loadEditVideo &&
      dataEditVideo &&
      dataEditVideo.editVideo &&
      dataEditVideo.editVideo.video
    ) {
      console.log("Success",dataEditVideo.video);
      navigate("/video/" + videoUuid);
    } else if (errorEditVideo) {
      console.log("Error", errorEditVideo, dataEditVideo);
    }    
  };  

  const handleCancel = () => {
    setTitle("");
    setContent("");
    setVideoUrl("");
  };

  const handlePreview = () => {
    setPreviewOpen(true);
    setShowFullVideo(false);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setShowFullVideo(false);
  };

  const handleClose = () => {
    setAlertPopup({ open: false, title: "" });
  };

  const userName = localStorage.getItem("userName") || "jessica";

  // Function to get the appropriate social media icon
  const getSocialMediaIcon = () => {
    const url = videoUrl.toLowerCase();

    if (url.includes("instagram")) {
      return <InstagramIcon sx={{ color: "#E4405F", mr: 0.5, mt: 1 }} />;
    } else if (url.includes("tiktok")) {
      return (
        <Box
          component="svg"
          sx={{ width: 24, height: 24, mr: 0.5, mt: 1 }}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"
            fill="#000000"
          />
        </Box>
      );
    } else if (url.includes("facebook") || url.includes("fb.watch")) {
      return <FacebookIcon sx={{ color: "#1877F2", mr: 0.5, mt: 1 }} />;
    } else if (url.includes("youtube") || url.includes("youtu.be")) {
      return <YouTubeIcon sx={{ color: "#FF0000", mr: 0.5, mt: 1 }} />;
    } else if (url.includes("vimeo")) {
      return (
        <Box
          component="svg"
          sx={{ width: 24, height: 24, mr: 0.5, mt: 1 }}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197a315.065 315.065 0 0 0 3.501-3.128C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.493 4.797l-.013.01z"
            fill="#1AB7EA"
          />
        </Box>
      );
    }
    return null;
  };

  return (
  <>
  {Object.keys(videoData).length > 0 ? (
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          minHeight: "100vh",
          py: 5,
          px: "100px",
          "@media (max-width: 1360px)": {
            px: "16px",
          },
        }}
      >
        <Helmet>
          <title>R3PLAY - Edit Video</title>
          <meta
            name="description"
            content="Share your video with the community"
          />
        </Helmet>

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 5,
            "@media (max-width: 856px)": {
              mb: 3,
            },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              minWidth: 48,
              minHeight: 48,
              backgroundColor: "white",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <VideocamOutlinedIcon
              sx={{
                color: "#EFB561",
                fontSize: "24px",
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontSize: "30px",
                fontWeight: "700",
                color: "#333",
                mb: 0.5,
                "@media (max-width: 856px)": {
                  fontSize: "24px",
                },
              }}
            >
              Edit Video
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#666",
                "@media (max-width: 856px)": {
                  fontSize: "13px",
                },
              }}
            >
              Paste a video link to embed it in your video.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "12px",
            p: 3,
            "@media (max-width: 856px)": {
              p: 2,
            },
            mt: 2,
          }}
        >
          {/* Form */}
          <Box>
            {/* Title/Summary Field */}
            <TextField
              fullWidth
              label="Title/Summary"
              placeholder="Enter a catchy title for your video"
              variant="outlined"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              sx={{
                mb: 3,
                "& .MuiInputBase-root": { height: "56px" },
              }}
            />

            {/* Video Text Field */}
            <TextField
              fullWidth
              label="Video Text"
              placeholder="Share your video description..."
              variant="outlined"
              required
              multiline
              rows={4}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              error={!!errors.content}
              helperText={errors.content}
              sx={{
                mb: 3,
              }}
            />

            {/* Video URL Field */}
            <TextField
              fullWidth
              label="Video URL"
              placeholder="Paste video link here"
              variant="outlined"
              required
              multiline
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              // onChange={(e) => {
              //   const value = e.target.value;
              //   setVideoUrl(value);

              //   if (!validateUrl(value)) {
              //     setVideoUrlError("Please enter a valid URL");
              //   } else {
              //     setVideoUrlError("");
              //   }
              // }}
              sx={{
                mb: 3,
                "& .MuiInputBase-root": {
                  minHeight: "56px",
                  alignItems: "flex-start",
                  "@media (max-width: 856px)": {
                    minHeight: "80px",
                  },
                },
                "& .MuiInputBase-input": {
                  "@media (min-width: 857px)": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                },
              }}
              InputProps={{
                endAdornment: videoUrl && getSocialMediaIcon(),
              }}
              error={!!errors.url}
              helperText={errors.url}
              // error={!!videoUrlError}
              // helperText={videoUrlError}
            />

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  "@media (max-width: 856px)": {
                    gap: 1,
                  },
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => handleSave(true)}
                  disabled={!title || !content || !videoUrl}
                  sx={{
                    height: "42px",
                    px: 3,
                    fontWeight: 500,
                    backgroundColor: "#EF5658",
                    "@media (max-width: 856px)": {
                      fontSize: "13px",
                      height: "30px",
                      px: 1,
                    },
                  }}
                >
                  Save & Post
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleSave(false)}
                  sx={{
                    backgroundColor: "#fff",
                    color: "#ef5658",
                    borderColor: "rgba(239, 86, 88, 0.5)",
                    borderRadius: "4px",
                    height: "42px",
                    px: 3,
                    fontWeight: 500,
                    "&:hover": {
                      borderColor: "#EF5658",
                      backgroundColor: "rgba(239, 86, 88, 0.1)",
                    },
                    "@media (max-width: 856px)": {
                      fontSize: "13px",
                      height: "30px",
                      px: 1,
                    },
                  }}
                >
                  Save as Draft
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    backgroundColor: "#fff",
                    color: "#000",
                    borderColor: "#000",
                    height: "42px",
                    px: 3,
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "#fff",
                      color: "#000",
                      borderColor: "#000",
                    },
                    "@media (max-width: 856px)": {
                      height: "30px",
                      fontSize: "13px",
                      px: 1,
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
              <Button
                variant="outlined"
                onClick={handlePreview}
                disabled={!title || !content || !videoUrl}
                sx={{
                  backgroundColor: "#fff",
                  color: "#ef5658",
                  borderColor: "rgba(239, 86, 88, 0.5)",
                  borderRadius: "4px",
                  height: "42px",
                  px: 3,
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#EF5658",
                    backgroundColor: "rgba(239, 86, 88, 0.1)",
                  },
                  "@media (max-width: 856px)": {
                    height: "30px",
                    fontSize: "13px",
                    px: 1,
                  },
                }}
              >
                Preview Video
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Preview Dialog */}
        <Dialog
          open={previewOpen}
          onClose={handleClosePreview}
          maxWidth={false}
          sx={{
            zIndex: 2100,
          }}
          PaperProps={{
            sx: {
              maxHeight: "650px",
              borderRadius: "12px",
              p: 1,
              width: "380px",
              maxWidth: "95vw",
              "@media (max-width: 1360px)": {
                px: 1,
              },
            },
          }}
        >
          <DialogContent sx={{ p: 0, overflowY: "visible" }}>
            {/* Dialog Header */}
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#333",
                  "@media (max-width: 856px)": {
                    fontSize: "24px",
                  },
                }}
              >
                Video Preview
              </Typography>
              <IconButton
                aria-label="close"
                onClick={handleClosePreview}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Video Card Preview */}
            <Card
              sx={{
                width: 288,
                height: 548,
                margin: "0 auto",
                border: "1px solid #EFB561",
                borderRadius: "12px",
                boxShadow: "none",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* User Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1,
                  px: 1,
                  backgroundColor: "#FCF2E3",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar src={sampleAvatar} sx={{ width: 40, height: 40 }} />
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body2" fontWeight="bold">
                      {userName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Just now
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Video Player */}
              <CardMedia
              sx={{
                width: 288,
                height: 320,
                position: "relative",
                backgroundColor: "#000",
                overflow: "hidden",

              }}
              >
                <ReactPlayer
                  url={videoUrl || "https://vimeo.com/595563572"}
                  width="100%"
                  height="100%"
                  controls
                  playing={false}
                  // config={{
                  //   vimeo: {
                  //     playerOptions: {
                  //       responsive: true,
                  //     }
                  //   }
                  // }}
                  style={{
                    display:"flex",
                    justifyContent:"center",
                    alignItems:"center",
                    // position: "absolute",
                    // top: 0,
                    // left: 0,
                  }}
                />
              </CardMedia>

              {/* Video Content */}
              <CardContent
                sx={{
                  pb: 1,
                  backgroundColor: "#FCF2E3",
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    color: "black",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minHeight: "2.8em",
                    lineHeight: "1.4em",
                  }}
                >
                  {title}
                </Typography>

                {!showFullVideo && content && content.length > 60 ? (
                  <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        color: "#666",
                        fontSize: "14px",
                        lineHeight: 1.4,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {content.substring(0, 60)}...{" "}
                      <Typography
                        component="span"
                        onClick={() => setShowFullVideo(true)}
                        sx={{
                          color: "#EFB561",
                          fontSize: "14px",
                          fontWeight: "500",
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        more
                      </Typography>
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      maxHeight: "80px",
                      overflowY: "auto",
                      overflowX: "hidden",
                      flexGrow: 1,
                      mb: 1,
                      pr: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontSize: "14px",
                        lineHeight: 1.4,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {content}
                    </Typography>
                  </Box>
                )}
              </CardContent>

              {/* Divider */}
              <Box
                sx={{
                  borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                  backgroundColor: "#FCF2E3",
                }}
              />

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  backgroundColor: "#FCF2E3",
                }}
              >
                {/* Left side - Like, Comment, Share icons */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <IconButton
                    size="small"
                    sx={{
                      color: "#9e9e9e",
                      p: 0,
                      pointerEvents: "none",
                    }}
                  >
                    <FavoriteBorderOutlinedIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    sx={{
                      color: "#9e9e9e",
                      p: 0,
                      pointerEvents: "none",
                    }}
                  >
                    <ModeCommentOutlinedIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    sx={{
                      color: "#9e9e9e",
                      p: 0,
                      pointerEvents: "none",
                    }}
                  >
                    <ShareOutlinedIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    sx={{
                      color: "#9e9e9e",
                      p: 0,
                      pointerEvents: "none",
                    }}
                  >
                    <CollectionsBookmarkIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Right side - Video icon */}
                <IconButton
                  sx={{
                    backgroundColor: "#EFB561",
                    color: "white",
                    borderRadius: "6px",
                    p: "3px",
                    pointerEvents: "none",
                    "&:hover": {
                      backgroundColor: "#EFB561",
                    },
                  }}
                >
                  <VideocamOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
            </Card>

            {/* OK Button */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Button
                variant="contained"
                onClick={handleClosePreview}
                sx={{
                  backgroundColor: "#EF5658",
                  color: "white",
                  borderRadius: "4px",
                  px: 5,
                  py: 1.5,
                  fontSize: "14px",
                  fontWeight: "500",
                  height: "32px",
                  textTransform: "uppercase",
                  "&:hover": {
                    backgroundColor: "#D33338",
                  },
                }}
              >
                OK
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
        
        <AlertPopup
          open={alertPopup.open}
          title={alertPopup.title}
          message=""
          confirmText="OK"
          cancelText= ""
          onConfirm={handleClose}
          onClose={handleClose}
        />
        <FloatingMenu left={floatMenuLeftMargin} />
      </Box>
  ) : (
      (isInvalidURL &&
        <Box sx={{ textAlign: "center", mb: 4, mt: 10 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: "30px",
              "@media (max-width: 856px)": {
                fontSize: "24px",
              },
              fontWeight: "700",
              color: "#333",
              mb: 1,
            }}
          >
            This URL is not valid.
          </Typography>
          <Button
          variant="contained"
          onClick={() => navigate("/dashboard")}
          sx={{
            height: "42px",
            px: 3,
            mt: 5,
            fontWeight: 500,
            backgroundColor: "#EF5658",
            "@media (max-width: 856px)": {
              fontSize: "13px",
              height: "30px",
              px: 1,
            },
          }}
        >
          OK
        </Button>
        </Box>
      )
  )}
  </>
  );
}
